/* Builds the Athos facet rail from a category response: accordions, chips, sort control.
   window.AthosFacets:
     facetListHtml(facets, { colorMap, openFields }) -> filter accordions
     chipsHtml(filterSummary)                         -> active-filter chips
     sortOptionsHtml(sorting, currentValue)           -> <option>s for the sort select
     readSortValue(params)                            -> 'field|dir' currently in the URL, or ''
*/
(() => {
  if (window.AthosFacets) return;

  const escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => escapeMap[ch]);

  const SHOW_LIMIT = 8;
  const SEARCH_MIN = 15;

  const CHEVRON = '<svg class="athos-facet__chevron icon" viewBox="0 0 24 24" stroke="currentColor" fill="none" role="presentation"><path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path></svg>';

  const isColorFacet = (facet) => (
    /colou?r/i.test(facet.field || '') || /colou?r/i.test(facet.label || '')
  );

  /* Reorder swatch values by a merchant priority list; the rest keep Athos's order. */
  const orderByPriority = (values, priority) => {
    if (!priority?.length) return values;
    const rank = new Map(priority.map((name, index) => [name, index]));
    const rankOf = (value) => {
      const key = String(value.label ?? '').toLowerCase().trim();
      return rank.has(key) ? rank.get(key) : Number.POSITIVE_INFINITY;
    };
    return values
      .map((value, index) => ({ value, index }))
      .sort((a, b) => (rankOf(a.value) - rankOf(b.value)) || (a.index - b.index))
      .map((entry) => entry.value);
  };

  const swatchStyle = (label, colorMap) => {
    const key = String(label || '').toLowerCase().trim();
    const entry = colorMap[key];
    const fallback = key.split(' ').pop().replace(/[^a-z0-9]+/g, '');
    const background = entry?.background || fallback || 'transparent';
    const image = entry?.backgroundImageUrl;
    return `--swatch-background: ${esc(background)};${image ? `--swatch-background-image: url(${esc(image)});` : ''}`;
  };

  const isDisabled = (value) => (Number(value.count) || 0) === 0 && !value.active;

  /* Show literal "true"/"false" boolean labels as Yes/No; the sent value is untouched. */
  const yesNoMap = { true: 'Yes', false: 'No' };
  const displayLabel = (label, booleanYesNo) => {
    const text = String(label ?? '');
    if (!booleanYesNo) return text;
    const key = text.toLowerCase().trim();
    return key === 'true' || key === 'false' ? yesNoMap[key] : text;
  };

  /* Classify a boolean-ish value as 'true' / 'false' (or '' when neither). */
  const BOOL_TRUE = new Set(['true', 'yes', '1']);
  const BOOL_FALSE = new Set(['false', 'no', '0']);
  const boolKind = (value) => {
    const key = String(value ?? '').toLowerCase().trim();
    return BOOL_TRUE.has(key) ? 'true' : BOOL_FALSE.has(key) ? 'false' : '';
  };
  /* A facet whose every value is boolean-ish (On Sale, Recycled, …). */
  const isBooleanFacet = (facet) => {
    const values = Array.isArray(facet.values) ? facet.values : [];
    return values.length > 0 && values.every((value) => boolKind(value.label) !== '');
  };

  const valueLi = (facet, value, index, { color, colorMap, overflow, booleanYesNo }) => {
    const id = `af-${esc(facet.field)}-${index}`;
    const count = Number(value.count) || 0;
    const disabled = isDisabled(value);
    const shown = displayLabel(value.label, booleanYesNo);
    const liClass = `athos-facet__value${color ? '' : ' flex items-center gap-3'}${overflow ? ' athos-facet__value--overflow' : ''}`;
    const input = `<input${color ? ' class="sr-only"' : ' class="checkbox"'} type="checkbox" id="${id}" data-athos-facet-input data-field="${esc(facet.field)}" data-value="${esc(value.value)}"${value.active ? ' checked' : ''}${disabled ? ' disabled' : ''} />`;
    const hasImage = color && Boolean(colorMap[String(value.label ?? '').toLowerCase().trim()]?.backgroundImageUrl);
    const body = color
      ? `<label class="color-swatch block relative${hasImage ? ' with-image' : ''}" for="${id}" style="${swatchStyle(value.label, colorMap)}" title="${esc(shown)} (${count})"><span class="sr-only">${esc(shown)}</span></label>`
      : `<label for="${id}" class="flex items-baseline gap-1 font-medium">${esc(shown)} <span class="text-sm${value.active ? '' : ' text-opacity'}">(${count})</span></label>`;
    return `<li class="${liClass}" data-athos-facet-value data-label="${esc(String(shown).toLowerCase())}">${input}${body}</li>`;
  };

  const facetAccordion = (facet, {
    colorMap = {},
    openFields,
    valueLimit = SHOW_LIMIT,
    searchThreshold = SEARCH_MIN,
    colorSwatches = true,
    forceCollapse,
    hideSingleValue = false,
    swatchPriority,
    swatchValueLimit = 30,
    booleanYesNo = false,
  } = {}) => {
    const rawValues = Array.isArray(facet.values) ? facet.values : [];
    if (!rawValues.length) return '';

    /* Boolean facets render as a single toggle: drop "No", keep only the "Yes" checkbox. */
    const booleanToggle = booleanYesNo && isBooleanFacet(facet);
    const usableValues = booleanToggle
      ? rawValues.filter((value) => boolKind(value.label) === 'true')
      : rawValues;
    if (!usableValues.length) return '';

    /* A single-option filter can't narrow anything; a boolean toggle's "Yes" is exempt. */
    if (hideSingleValue && !booleanToggle && usableValues.length <= 1) return '';

    /* Curated blocks can force swatches or list; otherwise auto-detect colour facets. */
    const color = facet.presentation === 'swatches' ? true
      : facet.presentation === 'list' ? false
      : (colorSwatches && isColorFacet(facet));

    /* Swatches follow the merchant's colour priority; lists keep Athos's order. */
    const values = color ? orderByPriority(usableValues, swatchPriority) : usableValues;

    /* Collapse precedence: block override, then section force, then Athos's collapse flag. */
    const collapsed = facet.collapseOverride !== undefined ? facet.collapseOverride
      : (forceCollapse === undefined ? Boolean(facet.collapse) : forceCollapse);
    const defaultOpen = color && facet.collapseOverride === undefined ? true : !collapsed;
    const open = openFields ? openFields.has(facet.field) : defaultOpen;
    /* Swatches get their own cap; text lists use the general one. */
    const limit = color ? swatchValueLimit : valueLimit;
    const overflowAt = limit > 0 ? limit : values.length;
    const hasOverflow = values.length > overflowAt;
    const items = values
      .map((value, i) => valueLi(facet, value, i, { color, colorMap, overflow: i >= overflowAt, booleanYesNo }))
      .join('');

    const listClass = color
      ? 'swatches flex items-center flex-wrap'
      : 'list grid gap-3d5';

    return `
      <details class="details athos-facet${open ? ' active' : ''}" is="accordion-details"${open ? ' open' : ''} data-athos-facet data-field="${esc(facet.field)}">
        <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
          <span class="text-base-xl font-bold">${esc(facet.label)}</span>
          ${CHEVRON}
        </summary>
        <div class="details__content">
          <ul class="${listClass} athos-facet__values" role="list" data-athos-facet-options>
            ${items}
          </ul>
          ${hasOverflow ? `<button type="button" class="link athos-facet__more" data-athos-facet-more aria-expanded="false">Show all (${values.length})</button>` : ''}
        </div>
      </details>`;
  };

  const facetListHtml = (facets, context = {}) => (
    (Array.isArray(facets) ? facets : [])
      .map((facet) => facetAccordion(facet, context))
      .join('')
  );

  const chipsHtml = (filterSummary, { booleanYesNo = false } = {}) => {
    const items = Array.isArray(filterSummary) ? filterSummary : [];
    if (!items.length) return '';

    const chips = items.map((entry) => {
      const value = entry.filterValue ?? entry.value;
      const shownValue = displayLabel(entry.filterValue, booleanYesNo);
      /* A boolean toggle's chip is just the facet name, not "name: Yes". */
      const label = booleanYesNo && boolKind(entry.filterValue) === 'true'
        ? entry.filterLabel
        : booleanYesNo
          ? `${entry.filterLabel}: ${shownValue}`
          : (entry.label || `${entry.filterLabel}: ${entry.filterValue}`);
      return `
        <button type="button" class="athos-chip removable-facet flex items-center gap-2 text-sm leading-none" data-athos-chip-remove data-field="${esc(entry.field)}" data-value="${esc(value)}">
          <span>${esc(label)}</span>
          <span class="athos-chip__remove" aria-hidden="true">&times;</span>
          <span class="sr-only">Remove ${esc(label)}</span>
        </button>`;
    }).join('');

    return `${chips}<button type="button" class="link athos-chip-clear text-sm" data-athos-clear-all>Clear all</button>`;
  };

  const sortOptionsHtml = (sorting, currentValue) => {
    const options = Array.isArray(sorting?.options) ? sorting.options : [];
    const current = currentValue || '';
    const rows = [`<option value=""${current === '' ? ' selected' : ''}>Featured</option>`];
    options.forEach((option) => {
      const direction = option.direction === 'desc' ? 'desc' : 'asc';
      const value = `${option.field}|${direction}`;
      rows.push(`<option value="${esc(value)}"${value === current ? ' selected' : ''}>${esc(option.label)}</option>`);
    });
    return rows.join('');
  };

  const readSortValue = (params) => {
    for (const key of params.keys()) {
      const field = key.match(/^sort\.(.+)$/);
      if (field) return `${field[1]}|${params.get(key) === 'desc' ? 'desc' : 'asc'}`;
    }
    return '';
  };

  window.AthosFacets = Object.freeze({
    facetListHtml,
    chipsHtml,
    sortOptionsHtml,
    readSortValue,
  });
})();
